import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Plus, Trash2, Copy, ExternalLink, Pencil, ArrowUpDown, Power, PowerOff } from 'lucide-react';
import { paylinksApi } from '../../api/endpoints/paylinks';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { FilterDropdown } from '../../components/ui/FilterDropdown';
import { DateFilterBar } from '../../components/ui/DateFilterBar';
import { SearchInput } from '../../components/ui/SearchInput';
import { Textarea } from '../../components/ui/Textarea';
import { Badge } from '../../components/ui/Badge';
import { Modal } from '../../components/ui/Modal';
import { PermissionGate } from '../../components/ui/PermissionGate';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { EmptyState } from '../../components/ui/EmptyState';
import { Pagination } from '../../components/ui/Pagination';
import { useToast } from '../../components/ui/Alert';
import { useDebounce } from '../../hooks/useDebounce';
import { usePagination } from '../../hooks/usePagination';
import { formatDate, formatCurrency, getStoreUrl } from '../../lib/formatters';
import { CURRENCIES } from '../../lib/constants';
import { useStore } from '../../contexts/StoreContext';
import { QuickPaylinkModal } from '../../components/paylinks/QuickPaylinkModal';
import type { Paylink } from '../../types/paylink';

function PaylinkAnalytics({ storeId, paylinkId }: { storeId: string; paylinkId: string }) {
  const { t } = useTranslation();
  const { data } = useQuery({
    queryKey: ['paylink-analytics', storeId, paylinkId],
    queryFn: () => paylinksApi.getAnalytics(storeId, paylinkId),
    enabled: !!storeId && !!paylinkId,
  });
  if (!data) return null;
  return (
    <p className="text-xs text-text-muted mb-3">
      {data.views} {t('paylinkAnalytics.views')} &ndash; {data.clicks} {t('paylinkAnalytics.clicks')} &ndash; {data.conversions} {t('paylinkAnalytics.conversions')}
    </p>
  );
}

export function PaylinkList() {
  const { storeId } = useParams<{ storeId: string }>();
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { currentStore } = useStore();
  const [deleteTarget, setDeleteTarget] = useState<Paylink | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editTarget, setEditTarget] = useState<Paylink | null>(null);

  // Filter state
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 200);
  const [sortDir, setSortDir] = useState<'desc' | 'asc'>('desc');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const { page, limit, goToPage } = usePagination({
    resetDeps: [debouncedSearch, sortDir, startDate, endDate],
  });

  // Edit form state (create flow uses the shared QuickPaylinkModal)
  const [formTitle, setFormTitle] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formAmount, setFormAmount] = useState<number>(0);
  const [formCurrency, setFormCurrency] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['paylinks', storeId, { page, limit, search: debouncedSearch, sort: sortDir, startDate, endDate }],
    queryFn: () => paylinksApi.list(storeId!, {
      page,
      limit,
      search: debouncedSearch || undefined,
      sort: sortDir !== 'desc' ? sortDir : undefined,
      startDate: startDate || undefined,
      endDate: endDate || undefined,
    }),
    enabled: !!storeId,
  });

  // Load edit data
  const { data: editData } = useQuery({
    queryKey: ['paylink', storeId, editTarget?._id],
    queryFn: () => paylinksApi.get(storeId!, editTarget!._id),
    enabled: !!storeId && !!editTarget,
  });

  useEffect(() => {
    if (editData?.paylink) {
      const pl = editData.paylink;
      setFormTitle(pl.title ?? '');
      setFormDescription(pl.description ?? '');
      setFormAmount(pl.amount ?? 0);
      setFormCurrency(pl.currency ?? '');
    }
  }, [editData]);

  const deleteMutation = useMutation({
    mutationFn: (id: string) => paylinksApi.delete(storeId!, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['paylinks', storeId] });
      toast('success', t('paylinks.deleteSuccess'));
      setDeleteTarget(null);
    },
    onError: () => toast('error', t('common.error')),
  });

  const updateMutation = useMutation({
    mutationFn: () => paylinksApi.update(storeId!, editTarget!._id, {
      title: formTitle,
      description: formDescription || undefined,
      amount: formAmount || undefined,
      currency: formCurrency || undefined,
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['paylinks', storeId] });
      toast('success', t('paylinks.updateSuccess'));
      closeEditModal();
    },
    onError: () => toast('error', t('common.error')),
  });

  const toggleActiveMutation = useMutation({
    mutationFn: ({ pl, next }: { pl: Paylink; next: boolean }) =>
      paylinksApi.update(storeId!, pl._id, { isActive: next }),
    onSuccess: (_, { next }) => {
      queryClient.invalidateQueries({ queryKey: ['paylinks', storeId] });
      toast('success', next
        ? t('paylinks.activatedSuccess', 'Paylink activated')
        : t('paylinks.deactivatedSuccess', 'Paylink deactivated'));
    },
    onError: () => toast('error', t('common.error')),
  });

  const paylinkUrl = (paylink: Paylink) =>
    paylink.mode === 'one-time'
      ? `https://00pays.com/paylink/${paylink.paylinkId}`
      : `${getStoreUrl(currentStore, storeId)}/paylink/${paylink.paylinkId}`;

  const copyLink = (paylink: Paylink) => {
    navigator.clipboard.writeText(paylinkUrl(paylink));
    toast('success', t('paylinks.linkCopied'));
  };

  function openEdit(pl: Paylink) {
    setEditTarget(pl);
    setFormTitle(pl.title ?? '');
    setFormDescription(pl.description ?? '');
    setFormAmount(pl.amount ?? 0);
    setFormCurrency(pl.currency ?? '');
  }

  function closeEditModal() { setEditTarget(null); }

  function handleEditSave() {
    if (!formTitle.trim()) return;
    updateMutation.mutate();
  }

  const paylinks = data?.paylinks ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.ceil(total / limit);

  if (isLoading) return <LoadingSpinner fullPage />;

  return (
    <div>
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-xl font-bold">{t('sidebar.paylinks')}</h1>
        <PermissionGate requires="paylinks.edit">
          <Button onClick={() => setShowCreateModal(true)}>
            <Plus className="h-4 w-4" />
            {t('paylinks.addPaylink')}
          </Button>
        </PermissionGate>
      </div>

      {/* Filters */}
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <SearchInput
          value={search}
          onChange={setSearch}
          placeholder={t('common.search', 'Search...')}
          className="w-64"
        />
        <FilterDropdown
          icon={ArrowUpDown}
          options={[
            { value: 'desc', label: t('common.newestFirst', 'Newest first') },
            { value: 'asc', label: t('common.oldestFirst', 'Oldest first') },
          ]}
          value={sortDir}
          onChange={(v) => setSortDir(v as 'desc' | 'asc')}
        />
        <DateFilterBar
          startDate={startDate}
          endDate={endDate}
          onStartDateChange={setStartDate}
          onEndDateChange={setEndDate}
        />
      </div>

      {paylinks.length === 0 ? (
        <EmptyState title={t('common.noData')} description={t('paylinks.empty')} />
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {paylinks.map((pl) => (
              <div
                key={pl._id}
                className={`group rounded-xl border bg-bg-secondary p-5 transition-colors hover:border-accent/30 ${
                  pl.isActive === false
                    ? 'border-dashed border-border-strong opacity-60'
                    : 'border-solid border-border-default'
                }`}
              >
                <div className="flex items-start justify-between mb-3 gap-2">
                  <h3 className="font-medium truncate">{pl.title || pl.paylinkId}</h3>
                  <div className="flex shrink-0 items-center gap-1">
                    {pl.mode === 'one-time' && (
                      <Badge variant={pl.consumedAt ? 'success' : 'default'}>
                        {pl.consumedAt
                          ? t('paylinks.statusPaid', 'Paid')
                          : t('paylinks.modeOneTime', 'One-time')}
                      </Badge>
                    )}
                    <Badge variant={pl.isActive !== false ? 'success' : 'default'}>
                      {pl.isActive !== false ? t('coupons.active') : t('coupons.inactive')}
                    </Badge>
                  </div>
                </div>
                {pl.amount != null && (
                  <p className="text-lg font-bold text-accent mb-2">
                    {formatCurrency(pl.amount, pl.currency || currentStore?.currency)}
                  </p>
                )}
                {pl.description && (
                  <p className="text-xs text-text-muted mb-3 line-clamp-2">{pl.description}</p>
                )}
                <PaylinkAnalytics storeId={storeId!} paylinkId={pl._id} />
                <p className="text-xs text-text-muted mb-4">
                  {pl.createdAt ? formatDate(pl.createdAt) : ''}
                </p>
                <div className="flex items-center gap-1 border-t border-border-default pt-3">
                  <button
                    onClick={() => copyLink(pl)}
                    className="rounded-lg p-1.5 text-text-muted hover:bg-bg-hover hover:text-accent"
                    title={t('paylinks.copyLink')}
                  >
                    <Copy className="h-4 w-4" />
                  </button>
                  <a
                    href={paylinkUrl(pl)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="rounded-lg p-1.5 text-text-muted hover:bg-bg-hover hover:text-accent"
                  >
                    <ExternalLink className="h-4 w-4" />
                  </a>
                  <PermissionGate requires="paylinks.edit">
                    <button
                      onClick={() => toggleActiveMutation.mutate({ pl, next: pl.isActive === false })}
                      disabled={toggleActiveMutation.isPending}
                      className="rounded-lg p-1.5 text-text-muted hover:bg-bg-hover hover:text-accent disabled:opacity-50"
                      title={pl.isActive === false
                        ? t('paylinks.activate', 'Activate')
                        : t('paylinks.deactivate', 'Deactivate')}
                    >
                      {pl.isActive === false
                        ? <Power className="h-4 w-4" />
                        : <PowerOff className="h-4 w-4" />}
                    </button>
                    <button
                      onClick={() => openEdit(pl)}
                      className="rounded-lg p-1.5 text-text-muted hover:bg-bg-hover hover:text-accent"
                      title={t('paylinks.editPaylink')}
                    >
                      <Pencil className="h-4 w-4" />
                    </button>
                  </PermissionGate>
                  <div className="flex-1" />
                  <PermissionGate requires="paylinks.edit">
                    <button
                      onClick={() => setDeleteTarget(pl)}
                      className="rounded-lg p-1.5 text-text-muted hover:bg-bg-hover hover:text-danger"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </PermissionGate>
                </div>
              </div>
            ))}
          </div>
          {totalPages > 1 && (
            <Pagination
              page={page}
              totalPages={totalPages}
              total={total}
              limit={limit}
              onPageChange={goToPage}
            />
          )}
        </>
      )}

      {/* Create modal — shared with /products Quick Paylink */}
      <QuickPaylinkModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        storeId={storeId!}
      />

      {/* Edit modal — title/description/amount/currency only (no product picker) */}
      <Modal
        isOpen={!!editTarget}
        onClose={closeEditModal}
        title={t('paylinks.editPaylink')}
        footer={
          <>
            <Button variant="secondary" onClick={closeEditModal}>{t('common.cancel')}</Button>
            <Button
              onClick={handleEditSave}
              isLoading={updateMutation.isPending}
              disabled={!formTitle.trim()}
            >
              {t('common.save')}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <Input label={t('common.name')} required value={formTitle} onChange={(e) => setFormTitle(e.target.value)} />
          <Textarea id="pl-desc" label={t('common.description')} rows={3} value={formDescription} onChange={(e) => setFormDescription(e.target.value)} />
          <div className="grid gap-4 sm:grid-cols-2">
            <Input label={t('common.price')} type="number" step="0.01" value={formAmount || ''} onChange={(e) => setFormAmount(Number(e.target.value) || 0)} />
            <Select
              label={t('paylinks.currency')}
              options={CURRENCIES.map(c => ({ value: c, label: c }))}
              value={formCurrency || currentStore?.currency || 'SAR'}
              onChange={(e) => setFormCurrency(e.target.value)}
            />
          </div>
        </div>
      </Modal>

      {/* Delete Modal */}
      <Modal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        title={t('common.confirm')}
        footer={
          <>
            <Button variant="secondary" onClick={() => setDeleteTarget(null)}>{t('common.cancel')}</Button>
            <Button
              variant="danger"
              isLoading={deleteMutation.isPending}
              onClick={() => deleteTarget && deleteMutation.mutate(deleteTarget._id)}
            >
              {t('common.delete')}
            </Button>
          </>
        }
      >
        <p className="text-sm text-text-secondary">{t('paylinks.deleteConfirm')}</p>
        <p className="mt-2 font-medium">{deleteTarget?.title || deleteTarget?.paylinkId}</p>
      </Modal>
    </div>
  );
}
